import clsx from "clsx";
import {
  AnimatePresence,
  type MotionProps,
  motion,
  type PanInfo,
  useDragControls,
} from "motion/react";
import {
  type ReactNode,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useIsDesktop } from "@/shared/hooks";
import { DialogCloseButton } from "@/shared/ui/overlay/DialogCloseButton";
import { getDialogAccessibilityProps } from "@/shared/ui/overlay/dialogAccessibility";
import { useDialogFocusTrap } from "@/shared/ui/overlay/dialogFocus";
import { ModalContext } from "@/shared/ui/overlay/modal-context";
import { stopOverlayWheelPropagation } from "@/shared/ui/overlay/overlayWheel";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  position?: "center" | "top" | "bottom" | "right";
  headerActions?: ReactNode;
  flickToClose?: boolean;
  width?: "default" | "settings";
} & (
  | { title: string; ariaLabel?: never }
  | { title?: undefined; ariaLabel: string }
);

export const Modal: React.FunctionComponent<ModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  ariaLabel,
  position = "center",
  headerActions,
  flickToClose = true,
  width = "default",
}: ModalProps) => {
  const { registerModal, unregisterModal } = useContext(ModalContext);
  const [depth, setDepth] = useState<number | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const titleId = useId();
  const dialogRef = useDialogFocusTrap<HTMLDivElement>(isOpen);
  const dragControls = useDragControls(); // ドラッグ制御用
  const isDesktop = useIsDesktop();
  // PCでは全幅ボトムシートにせず中央ダイアログとして表示する
  const promotedFromBottom = isDesktop && position === "bottom";
  const effectivePosition = promotedFromBottom ? "center" : position;

  // モーダルが開いたときに深さとcloseハンドラーを登録
  useEffect(() => {
    if (isOpen) {
      const registration = registerModal(() => onCloseRef.current());
      let disposed = false;
      queueMicrotask(() => {
        if (!disposed) setDepth(registration.depth);
      });
      return (): void => {
        disposed = true;
        unregisterModal(registration.id);
        queueMicrotask(() => setDepth(null));
      };
    }
  }, [isOpen, registerModal, unregisterModal]);

  // 下フリックでモーダルを閉じる処理
  const handleDragEnd = (
    _event: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo,
  ): void => {
    // 下方向に100px以上ドラッグ、または速度が500以上の場合に閉じる
    if (info.offset.y > 100 || info.velocity.y > 500) {
      onClose();
    }
  };

  const getMotionProps = (): MotionProps => {
    switch (effectivePosition) {
      case "bottom":
        return {
          initial: { opacity: 0, y: "100%" },
          animate: { opacity: 1, y: 0 },
          exit: { opacity: 0, y: "100%" },
        };
      case "right":
        return {
          initial: { opacity: 0, x: "100%" },
          animate: { opacity: 1, x: 0 },
          exit: { opacity: 0, x: "100%" },
        };
      default:
        return {
          initial: { opacity: 0, scale: 0.95 },
          animate: { opacity: 1, scale: 1 },
          exit: { opacity: 0, scale: 0.95 },
        };
    }
  };

  // ボトムモーダルでフリック閉じが有効な場合のドラッグ設定
  const getDragProps = (): MotionProps => {
    // position="bottom"かつflickToCloseがtrueのときのみ有効
    if (effectivePosition === "bottom" && flickToClose) {
      return {
        drag: "y" as const, // y方向のみドラッグ可能
        dragConstraints: { top: 0, bottom: 0 }, // ドラッグ範囲を上下に制限
        dragElastic: { top: 0, bottom: 0.8 }, // 下部の弾力性を調整
        onDragEnd: handleDragEnd, // ドラッグ終了時の処理
        dragControls, // ドラッグ制御用
      };
    }
    return {};
  };

  // 深さに応じたz-indexを計算（各モーダルレベルで20ずつ増やす）
  const baseZIndex = 40;
  const zIndexOffset = (depth ?? 0) * 20;
  const backdropZIndex = baseZIndex + zIndexOffset;
  const modalZIndex = baseZIndex + 10 + zIndexOffset;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/60"
            style={{ zIndex: backdropZIndex }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            onWheel={stopOverlayWheelPropagation}
          />
          <motion.div
            ref={dialogRef}
            tabIndex={-1}
            {...getDialogAccessibilityProps(titleId, Boolean(title), ariaLabel)}
            className={clsx(
              "fixed bg-card shadow-2xl",
              effectivePosition === "center" &&
                "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-xl w-[calc(100%-2rem)] max-h-[85svh] overflow-hidden flex flex-col",
              effectivePosition === "top" &&
                "top-[7.5svh] left-1/2 -translate-x-1/2 rounded-xl w-[calc(100%-2rem)] max-h-[85svh] overflow-hidden flex flex-col",
              (effectivePosition === "center" || effectivePosition === "top") &&
                (width === "settings" ? "max-w-[640px]" : "max-w-lg"),
              promotedFromBottom && "lg:max-w-2xl",
              effectivePosition === "bottom" &&
                "bottom-0 left-0 right-0 rounded-t-2xl max-h-[85svh] overflow-hidden",
              effectivePosition === "bottom" &&
                flickToClose &&
                "grid grid-flow-row grid-rows-[repeat(2,_max-content)_1fr]",
              effectivePosition === "bottom" &&
                !flickToClose &&
                "flex flex-col",
              effectivePosition === "right" &&
                "top-0 right-0 bottom-0 w-80 max-w-full overflow-y-auto",
            )}
            style={{
              zIndex: modalZIndex,
              touchAction:
                effectivePosition === "bottom" && flickToClose
                  ? "none"
                  : "auto",
            }}
            {...getMotionProps()}
            {...getDragProps()}
            transition={{ duration: 0.2, ease: "easeOut" }}
            onWheel={stopOverlayWheelPropagation}
          >
            {/* スワイプハンドル（ボトムモーダルでフリック閉じ有効時のみ表示） */}
            {effectivePosition === "bottom" && flickToClose && (
              <div className="flex cursor-grab justify-center pb-1 pt-2 before:h-1 before:w-10 before:rounded-full before:bg-muted-foreground/30 before:content-[''] active:cursor-grabbing" />
            )}
            {title && (
              <header className="flex items-center justify-between px-4 py-3 border-b border-border">
                <h2
                  id={titleId}
                  className="text-lg font-semibold text-foreground"
                >
                  {title}
                </h2>
                <fieldset
                  aria-label="ダイアログ操作"
                  className="m-0 flex min-w-0 items-center gap-1 border-0 p-0"
                >
                  {headerActions}
                  <DialogCloseButton
                    onClose={onClose}
                    className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  />
                </fieldset>
              </header>
            )}
            <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
};
